import React, { Children } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight, ShoppingBag } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { bookData } from './bookData';
function getCartItemImage(item: {
  productId: string;
  name: string;
  type: string;
}): string | undefined {
  const name = item.name?.trim().toLowerCase();
  if (item.type === 'book') {
    const b = bookData.find(x => x.id === item.productId) ?? bookData.find(x => x.title?.trim().toLowerCase() === name);
    return b?.coverImage;
  }
  const p = shopData.find(s => s.id === item.productId) ?? shopData.find(s => s.name?.trim().toLowerCase() === name);
  return p?.image;
}
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
export function PreOrderConfirmation() {
  const {
    user,
    loading
  } = useAuth();
  const {
    items
  } = useCart();
  // Guard: if someone deep-links here without a session, send them to shop.
  if (!loading && !user) {
    return <Navigate to="/shop" replace data-id="element-2120" />;
  }
  return <div className="w-full min-h-screen bg-[#F9F9F9]" data-id="element-2121">
      {/* HERO */}
      <section className="pt-32 pb-10 px-6" data-id="element-2122">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2123">
          <motion.div initial="hidden" animate="visible" variants={{
          visible: {
            transition: {
              staggerChildren: 0.1
            }
          }
        }} data-id="element-2124">
            <motion.div variants={fadeUp} className="w-20 h-20 rounded-full bg-tealslate/10 mx-auto mb-6 flex items-center justify-center" data-id="element-2125">
              <CheckCircle2 className="w-9 h-9 text-tealslate" strokeWidth={2} data-id="element-2126" />
            </motion.div>
            <motion.h1 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-2127">
              Thank you for your pre-order.
            </motion.h1>
            <motion.p variants={fadeUp} className="text-sm font-medium text-[#5A5A5A] max-w-md mx-auto leading-relaxed" data-id="element-2128">
              We'll notify you by email when it's time to check out and complete
              your order. No payment has been taken.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* DETAILS */}
      <section className="pb-24 px-6" data-id="element-2129">
        <div className="max-w-2xl mx-auto" data-id="element-2130">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="bg-white border border-[#E5E5E5] shadow-sm rounded-xl p-6 md:p-8 mb-6" data-id="element-2131">
            <h2 className="text-xl font-bold text-[#111111] tracking-tight mb-5" data-id="element-2132">
              What happens next?
            </h2>
            <ul className="space-y-4 text-sm text-[#5A5A5A] leading-relaxed" data-id="element-2133">
              <li className="flex gap-3" data-id="element-2134">
                <span className="text-tealslate font-bold shrink-0" data-id="element-2135">1.</span>
                <span data-id="element-2136">
                  A confirmation email is on its way to{' '}
                  <span className="font-bold text-[#111111]" data-id="element-2137">
                    {user?.email}
                  </span>
                  .
                </span>
              </li>
              <li className="flex gap-3" data-id="element-2138">
                <span className="text-tealslate font-bold shrink-0" data-id="element-2139">2.</span>
                <span data-id="element-2140">
                  Your reserved items stay saved in your cart — no need to
                  re-add them later.
                </span>
              </li>
              <li className="flex gap-3" data-id="element-2141">
                <span className="text-tealslate font-bold shrink-0" data-id="element-2142">3.</span>
                <span data-id="element-2143">
                  We'll email you as soon as inventory is ready so you can
                  complete your order.
                </span>
              </li>
            </ul>
          </motion.div>

          {items.length > 0 && <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="bg-white border border-[#E5E5E5] shadow-sm rounded-xl p-6 md:p-8 mb-8" data-id="element-2144">
              <p className="text-sm font-bold text-[#111111] mb-5" data-id="element-2145">
                Reserved items
              </p>
              <ul className="space-y-4" data-id="element-2146">
                {items.map(item => {
              const image = getCartItemImage(item);
              return <li key={item.productId} className="flex items-start gap-4 text-sm" data-id="element-2147">
                      {image && <div className="w-24 h-24 sm:w-28 sm:h-28 shrink-0 flex items-center justify-center" data-id="element-2148">
                          <img src={image} alt="" className="max-w-full max-h-full object-contain" data-id="element-2149" />
                        </div>}
                      <div className="flex-1 min-w-0" data-id="element-2150">
                        <p className="font-bold text-[#111111]" data-id="element-2151">{item.name}</p>
                        <p className="text-sm text-[#5A5A5A] mt-1" data-id="element-2152">
                          Qty {item.quantity}
                        </p>
                      </div>
                    </li>;
            })}
              </ul>
            </motion.div>}

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="flex flex-col sm:flex-row gap-4" data-id="element-2153">
            <Link to="/shop" className="flex-1 px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-2154">
              <ShoppingBag className="w-4 h-4" data-id="element-2155" />
              Back to Shop
            </Link>
            <Link to="/" className="flex-1 px-8 py-4 bg-white border border-[#E5E5E5] text-[#111111] text-sm font-bold hover:border-tealslate hover:text-tealslate transition-colors rounded-md shadow-sm inline-flex items-center justify-center gap-2" data-id="element-2156">
              Return Home <ArrowRight className="w-4 h-4" data-id="element-2157" />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>;
}